import { CompiledChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import Highcharts from 'highcharts'
import type DimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/DimensionResolver'
import { Incident } from '@/types/Incident'
import FallbackDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/FallbackDimensionResolver'
import { IncidentCameraDimensionResolver } from '@/composables/charts/influx-charts/dimension-resolvers/incidents/IncidentCameraDimensionResolver'
import { DatabaseEntryChartResultPipeline, SegmentBound } from '@/composables/charts/DatabaseEntryChartResultPipeline'

type TimestampMode = 'opened' | 'closed'

export class IncidentChartResultPipeline extends DatabaseEntryChartResultPipeline<Incident> {
  /**
   * Define incident-specific resolver classes
   */
  protected getSupportedResolverClasses(): (typeof DimensionResolver)[] {
    return [IncidentCameraDimensionResolver, FallbackDimensionResolver]
  }

  /**
   * Extract timestamp from incident based on mode
   */
  protected getTimestampFromEntry(incident: Incident, timestampProperty: string): number | null {
    // For incidents, timestampProperty is actually the mode
    const mode = timestampProperty as TimestampMode

    if (mode === 'opened') {
      return incident.firstEventTimestamp
    }

    if (mode === 'closed' && incident.state === 'closed') {
      return incident.lastEventTimestamp
    }

    return null
  }

  /**
   * Override to include additional incident-specific criteria
   */
  protected shouldIncludeEntry(incident: Incident, bound: SegmentBound, timestampProperty: string): boolean {
    const mode = timestampProperty as TimestampMode

    if (mode === 'opened') {
      return this.isTimestampInBounds(incident.firstEventTimestamp, bound)
    }

    return mode === 'closed' && incident.state === 'closed' && this.isTimestampInBounds(incident.lastEventTimestamp, bound)
  }

  /**
   * Public method with incident-specific signature for backward compatibility
   */
  async prepareData(
    incidents: Incident[],
    timestampModeOrProperty: TimestampMode | string,
    groupByDimensions: string[] = [],
    compiledTimeSetup: CompiledChartTimeSetup
  ): Promise<Highcharts.SeriesOptionsType[]> {
    // Call parent with incidents as entries and mode as timestampProperty
    return super.prepareData(incidents, timestampModeOrProperty, groupByDimensions, compiledTimeSetup)
  }

  /**
   * Alternative method signature for clearer incident-specific usage
   */
  async prepareIncidentData(
    incidents: Incident[],
    timestampMode: TimestampMode,
    groupByDimensions: string[] = [],
    compiledTimeSetup: CompiledChartTimeSetup
  ): Promise<Highcharts.SeriesOptionsType[]> {
    return this.prepareData(incidents, timestampMode, groupByDimensions, compiledTimeSetup)
  }
}
